// src/OnboardingPage.js
import React from 'react'
import { useNavigate } from 'react-router-dom'

const OnboardingPage = () => {
  const navigate = useNavigate()

  const handleStart = () => {
    navigate('/form')
  }

  return (
    <div className="onboarding-shell">
      <div className="onboarding-card">
        {/* Hero */}
        <header className="onboarding-hero">
          <h1>Welcome to the Decision Fit Canvas</h1>
          <p>
            This tool helps you reflect on your organisation’s readiness for digital change.
            It helps you assess your capability, culture, strategy, and other critical factors
            that will affect the success of your digital projects.
          </p>
        </header>

        {/* What this tool helps with */}
        <section className="onboarding-section highlight-box">
          <h2>What this tool helps with</h2>
          <p>
            Cultural organisations often leap into digital decisions without fully considering how well
            the solution fits their culture, capability, and readiness. This can lead to frustration, waste,
            and missed potential, even when the solution itself is “right.”
          </p>
          <ul>
            <li>Reflect on your organisation’s readiness across ten essential dimensions</li>
            <li>Spot gaps between ambition and capacity</li>
            <li>Align internal teams around strengths and risks</li>
          </ul>
          <p className="small-note">
            It’s not a scorecard. There’s no pass/fail. It’s a mirror: helping you see what’s strong,
            what needs work, and what that means for your next step.
          </p>
        </section>

        {/* How to use it */}
        <section className="onboarding-section">
          <h2>How to use it</h2>
          <ol className="step-list">
            <li>
              <span className="step-number">1</span>
              Choose a decision you’re facing — a platform, tool, partnership, or project approach.
            </li>
            <li>
              <span className="step-number">2</span>
              Score your organisation across ten dimensions.
            </li>
            <li>
              <span className="step-number">3</span>
              See your scores plotted on the radar chart to understand strengths and risks.
            </li>
            <li>
              <span className="step-number">4</span>
              Reflect as a team or solo using the prompts.
            </li>
          </ol>
        </section>

        {/* Best used when */}
        <section className="onboarding-section cards-grid">
          <h2>Best used when</h2>
          <div className="card-row">
            <div className="info-card">
              <h3>Strategic planning</h3>
              <ul>
                <li>You’re applying for digital funding</li>
                <li>You’re scoping a new system or supplier brief</li>
                <li>Your team is unsure what kind of solution to pursue</li>
              </ul>
            </div>
            <div className="info-card">
              <h3>Learning & growth</h3>
              <ul>
                <li>You’ve tried something before and it didn’t land</li>
                <li>You want a structured way to align perspectives</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Solo use */}
        <section className="onboarding-section soft-panel">
          <h2>If you're using this on your own…</h2>
          <p>
            That’s fine too. The act of reflection is always useful and may help you frame better questions to take to your team, board, or suppliers.
          </p>
        </section>

        {/* Can be used as */}
        <section className="onboarding-section mini-cards">
          <h2>Can be used as</h2>
          <div className="mini-card-row">
            <div className="mini-card">A pre-project reflection tool</div>
            <div className="mini-card">A cross-team workshop starter</div>
            <div className="mini-card">A funder discussion aid</div>
            <div className="mini-card">A post-mortem or evaluation lens</div>
          </div>
        </section>

        {/* Button */}
        <div className="onboarding-footer">
          <button onClick={handleStart} className="btn-primary">
            Start the assessment
          </button>
        </div>
      </div>
    </div>
  )
}

export default OnboardingPage